import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { toast } from '../../../lib/toast';
import { API, auth, inputCls, Field, Modal, PrimaryBtn, errMsg, fmtDate } from '../finance/shared';

const STATUS_LABELS = { draft: 'مسودة', sent: 'مرسل', viewed: 'تمت المشاهدة', accepted_contact: 'وافق على التواصل', rejected: 'رفض', interview_requested: 'طلب مقابلة', interview_scheduled: 'مقابلة مجدولة', offer_sent: 'تم إرسال عرض', offer_accepted: 'قبل العرض', offer_rejected: 'رفض العرض', hired: 'تم التعاقد', cancelled: 'ملغي', expired: 'منتهي' };
const STATUS_COLORS = { accepted_contact: 'bg-teal-50 text-teal-700', rejected: 'bg-red-50 text-red-600', offer_accepted: 'bg-teal-50 text-teal-700', offer_rejected: 'bg-red-50 text-red-600', hired: 'bg-teal-50 text-teal-700', cancelled: 'bg-slate-100 text-slate-500' };
const CAN_INTERVIEW = ['accepted_contact', 'interview_requested'];
const CAN_OFFER = ['accepted_contact', 'interview_requested', 'interview_scheduled'];
const EMPTY_OFFER = { title: '', sport: 'football', season: '', start_date: '', salary_amount: '', terms_note: '' };

export default function CoachOrgRequestsTab() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [offerModal, setOfferModal] = useState(null);
  const [offerForm, setOfferForm] = useState(EMPTY_OFFER);

  const load = useCallback(async () => {
    setLoading(true);
    try { const res = await axios.get(`${API}/coaches/requests`, auth()); setItems(res.data); } catch { setItems([]); }
    setLoading(false);
  }, []);
  useEffect(() => { load(); }, [load]);

  const setStatus = async (id, status) => {
    try { await axios.put(`${API}/coaches/requests/${id}/status`, { status }, auth()); toast.success('تم التحديث'); load(); }
    catch (err) { toast.error(errMsg(err, 'فشل التحديث')); }
  };

  const submitOffer = async (e) => {
    e.preventDefault();
    try {
      await axios.post(`${API}/coaches/offers`, { request_id: offerModal.id, ...offerForm, salary_amount: offerForm.salary_amount ? Number(offerForm.salary_amount) : null }, auth());
      toast.success('تم إرسال العرض للمدرب');
      setOfferModal(null); setOfferForm(EMPTY_OFFER); load();
    } catch (err) { toast.error(errMsg(err, 'فشل إرسال العرض')); }
  };

  return (
    <div className="space-y-4" data-testid="coach-org-requests-tab">
      {loading ? <p className="text-sm text-slate-400">جارِ التحميل...</p> : items.length === 0 ? (
        <p className="rounded-xl border border-dashed border-slate-200 py-10 text-center text-sm text-slate-400" data-testid="coach-org-requests-empty">لا توجد طلبات تواصل مرسلة بعد</p>
      ) : (
        <div className="space-y-2">
          {items.map((r) => (
            <div key={r.id} className="rounded-xl border border-slate-200 bg-white p-4" data-testid="coach-org-request-row">
              <div className="flex items-start justify-between">
                <div>
                  <div className="text-sm font-bold text-slate-800">{r.coach_name}</div>
                  <div className="text-xs text-slate-500">{r.position_title || '—'} · {fmtDate(r.created_at)}</div>
                </div>
                <span className={`rounded-full px-2 py-0.5 text-xs font-semibold ${STATUS_COLORS[r.status] || 'bg-slate-100 text-slate-500'}`}>{STATUS_LABELS[r.status] || r.status}</span>
              </div>
              <div className="mt-3 flex flex-wrap gap-2">
                {CAN_INTERVIEW.includes(r.status) && (
                  <button onClick={() => setStatus(r.id, 'interview_requested')} className="rounded-lg border border-slate-200 px-3 py-1.5 text-xs font-semibold text-slate-600">طلب مقابلة</button>
                )}
                {CAN_OFFER.includes(r.status) && (
                  <button onClick={() => setOfferModal(r)} className="rounded-lg bg-[#16233b] px-3 py-1.5 text-xs font-semibold text-white" data-testid="coach-org-send-offer-btn">إرسال عرض</button>
                )}
                {!['rejected', 'cancelled', 'expired', 'hired', 'offer_rejected'].includes(r.status) && (
                  <button onClick={() => setStatus(r.id, 'cancelled')} className="rounded-lg border border-red-200 px-3 py-1.5 text-xs font-semibold text-red-600">إلغاء الطلب</button>
                )}
              </div>
            </div>
          ))}
        </div>
      )}

      <Modal open={Boolean(offerModal)} title={`إرسال عرض لـ ${offerModal?.coach_name || ''}`} onClose={() => setOfferModal(null)} wide>
        <form onSubmit={submitOffer} className="grid grid-cols-1 gap-3 sm:grid-cols-2">
          <Field label="عنوان الدور" required><input required className={inputCls} value={offerForm.title} onChange={(e) => setOfferForm({ ...offerForm, title: e.target.value })} data-testid="offer-input-title" /></Field>
          <Field label="الموسم"><input className={inputCls} value={offerForm.season} onChange={(e) => setOfferForm({ ...offerForm, season: e.target.value })} data-testid="offer-input-season" /></Field>
          <Field label="تاريخ البداية" required><input required type="date" className={inputCls} value={offerForm.start_date} onChange={(e) => setOfferForm({ ...offerForm, start_date: e.target.value })} data-testid="offer-input-start-date" /></Field>
          <Field label="الراتب المعروض (اختياري)"><input type="number" className={inputCls} value={offerForm.salary_amount} onChange={(e) => setOfferForm({ ...offerForm, salary_amount: e.target.value })} data-testid="offer-input-salary" /></Field>
          <div className="col-span-full"><Field label="ملاحظات وشروط العرض"><textarea rows={3} className={inputCls} value={offerForm.terms_note} onChange={(e) => setOfferForm({ ...offerForm, terms_note: e.target.value })} data-testid="offer-input-terms" /></Field></div>
          <div className="col-span-full flex justify-end gap-2">
            <button type="button" onClick={() => setOfferModal(null)} className="rounded-lg border border-slate-200 px-4 py-2 text-sm text-slate-600">إلغاء</button>
            <PrimaryBtn type="submit" data-testid="offer-submit-btn">إرسال العرض</PrimaryBtn>
          </div>
        </form>
      </Modal>
    </div>
  );
}
